import * as React from "react";
import {DetailedHTMLProps, FC, useCallback, useEffect, useReducer, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {FormInputText} from "../../pages/shop/wholesale/form/FormInputText";
import {FormInputSelectMultiple} from "../../pages/shop/wholesale/form/FormInputSelectMultiple";
import {FormInputSelect} from "../../pages/shop/wholesale/form/FormInputSelect";
import {FilterAlt, Sort} from "@mui/icons-material";
import {Button, CircularProgress, Drawer, Grid, Stack, useMediaQuery} from "@mui/material";
import {RangeFilter, RangeFilterValue, RangeSort} from "./RangeListTypes";
import makeStyles from "@mui/styles/makeStyles";
import {TitleText} from "../../layout/Typography";
import {RangeCustomizer} from "./RangeCustomizer";

/** Debounce delay for filter selection changes */
const FILTER_DEBOUNCE_MS = 400;

/** Debounce delay for search input before triggering search */
const SEARCH_DEBOUNCE_MS = 1800;

/** Cooldown period after search to prevent duplicate searches */
const SEARCH_COOLDOWN_MS = 2000;

/** Short delay to detect when user stops typing */
const INPUT_CHANGE_DEBOUNCE_MS = 100;

export type RangeHeaderData = {
   search: string;
   filterOr: {
      [id: string]: string[]
   };
   filterAnd: {
      [id: string]: string[]
   };
   sort?: string;
}

export type RangeHeaderProps = DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
   globalId: string
   count: number | undefined;
   filters?: RangeFilter<any>[];
   sorts?: RangeSort<any>[];
   searchLabel?: string;
   headerData: RangeHeaderData;
   onHeaderChanged: (headerData: RangeHeaderData) => void;
   isSearchLoading?: boolean;
}

// RangeHeader State Management
interface RangeHeaderState {
   filterPanel: boolean;
   localSearchValue: string;
   searchInputChanging: boolean;
   lastSubmittedSearch: string;
   isActiveSearch: boolean;
}

type RangeHeaderAction =
   | { type: 'TOGGLE_FILTER_PANEL'; payload?: boolean }
   | { type: 'SET_LOCAL_SEARCH'; payload: string }
   | { type: 'SET_SEARCH_INPUT_CHANGING'; payload: boolean }
   | { type: 'SET_LAST_SUBMITTED_SEARCH'; payload: string }
   | { type: 'SET_ACTIVE_SEARCH'; payload: boolean }
   | { type: 'SUBMIT_SEARCH'; payload: string }
   | { type: 'RESET_SEARCH'; payload: string };

const initialRangeHeaderState: RangeHeaderState = {
   filterPanel: false,
   localSearchValue: '',
   searchInputChanging: false,
   lastSubmittedSearch: '',
   isActiveSearch: false
};

function rangeHeaderReducer(state: RangeHeaderState, action: RangeHeaderAction): RangeHeaderState {
   switch (action.type) {
      case 'TOGGLE_FILTER_PANEL':
         return { ...state, filterPanel: action.payload ?? !state.filterPanel };
      case 'SET_LOCAL_SEARCH':
         return { ...state, localSearchValue: action.payload };
      case 'SET_SEARCH_INPUT_CHANGING':
         return { ...state, searchInputChanging: action.payload };
      case 'SET_LAST_SUBMITTED_SEARCH':
         return { ...state, lastSubmittedSearch: action.payload };
      case 'SET_ACTIVE_SEARCH':
         return { ...state, isActiveSearch: action.payload };
      case 'SUBMIT_SEARCH':
         return {
            ...state,
            lastSubmittedSearch: action.payload,
            isActiveSearch: true,
            searchInputChanging: false
         };
      case 'RESET_SEARCH':
         return {
            ...state,
            localSearchValue: action.payload,
            lastSubmittedSearch: action.payload
         };
      default:
         return state;
   }
}

export const RangeHeader: FC<RangeHeaderProps> = ({
                                                   globalId,
                                                   count,
                                                   filters,
                                                   sorts,
                                                   searchLabel,
                                                   headerData,
                                                   onHeaderChanged,
                                                   isSearchLoading,
                                                   ...props
                                                }) => {
   const [state, dispatch] = useReducer(rangeHeaderReducer, {
      ...initialRangeHeaderState,
      localSearchValue: headerData.search || '',
      lastSubmittedSearch: headerData.search || ''
   });

   // Refs to track timers for cleanup
   const searchInputTimerRef = useRef<NodeJS.Timeout>();
   const enterKeyTimerRef = useRef<NodeJS.Timeout>();
   const filterDebounceTimerRef = useRef<NodeJS.Timeout>();

   const classes = useStyles();
   const isDesktopLG = useMediaQuery('(min-width:1250px)');
   const isDesktopMD = useMediaQuery('(min-width:800px)');

   const methods = useForm<RangeHeaderData>({
      defaultValues: headerData
   });

   const {watch, getValues, reset, setValue} = methods;

   // Watch the react-hook-form search value for debounce
   const formSearchValue = watch('search');
   const debouncedSearch = useDebounce(formSearchValue, SEARCH_DEBOUNCE_MS);

   // Cleanup all timers on unmount
   useEffect(() => {
      return () => {
         if (searchInputTimerRef.current) {
            clearTimeout(searchInputTimerRef.current);
         }
         if (enterKeyTimerRef.current) {
            clearTimeout(enterKeyTimerRef.current);
         }
         if (filterDebounceTimerRef.current) {
            clearTimeout(filterDebounceTimerRef.current);
         }
      };
   }, []);

   // Initialize local search value when headerData changes (but not during typing or active search)
   useEffect(() => {
      if (!state.searchInputChanging &&
         !state.isActiveSearch &&
         headerData.search !== state.lastSubmittedSearch) {
         dispatch({ type: 'RESET_SEARCH', payload: headerData.search || '' });
      }
   }, [headerData.search, state.searchInputChanging, state.isActiveSearch, state.lastSubmittedSearch]);

   // Update the form when headerData prop changes
   useEffect(() => {
      const currentValues = getValues();

      const searchToKeep = state.searchInputChanging ||
      (headerData.search === '' && currentValues.search !== '')
         ? currentValues.search
         : headerData.search;

      // Reset with keepDefaultValues: false to ensure all fields are cleared
      // This is important when switching tabs - filters not in the new headerData should be cleared
      reset({
         ...headerData,
         search: searchToKeep
      }, {keepDefaultValues: false});
   }, [headerData, reset, getValues]);

   // Handle debounced search changes
   useEffect(() => {
      if (headerData.search === debouncedSearch ||
         state.isActiveSearch ||
         (debouncedSearch === '' && state.localSearchValue !== '')) {
         return;
      }

      dispatch({ type: 'SUBMIT_SEARCH', payload: debouncedSearch });

      onHeaderChanged({
         ...headerData,
         search: debouncedSearch
      });

      const timer = setTimeout(() => {
         dispatch({ type: 'SET_ACTIVE_SEARCH', payload: false });
      }, SEARCH_COOLDOWN_MS);

      return () => clearTimeout(timer);
   }, [debouncedSearch, headerData, onHeaderChanged, state.localSearchValue, state.isActiveSearch]);

   // Custom handler for search input changes
   const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      dispatch({ type: 'SET_SEARCH_INPUT_CHANGING', payload: true });

      dispatch({ type: 'SET_LOCAL_SEARCH', payload: newValue });
      setValue('search', newValue);

      if (searchInputTimerRef.current) {
         clearTimeout(searchInputTimerRef.current);
      }

      searchInputTimerRef.current = setTimeout(() => {
         dispatch({ type: 'SET_SEARCH_INPUT_CHANGING', payload: false });
      }, INPUT_CHANGE_DEBOUNCE_MS);
   };

   // Handle Enter key press
   const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
         e.preventDefault();

         const currentSearchValue = getValues('search');

         dispatch({ type: 'SUBMIT_SEARCH', payload: currentSearchValue });

         onHeaderChanged({
            ...headerData,
            search: currentSearchValue
         });

         const inputElement = e.target as HTMLInputElement;
         inputElement.select();

         if (enterKeyTimerRef.current) {
            clearTimeout(enterKeyTimerRef.current);
         }

         enterKeyTimerRef.current = setTimeout(() => {
            dispatch({ type: 'SET_ACTIVE_SEARCH', payload: false });
         }, SEARCH_COOLDOWN_MS);
      }
   }

   // Enhanced search input with better indicators and custom change handler
   const renderSearchInput = () => (
      <FormInputText
         name={"search"}
         autoComplete={"off"}
         label={searchLabel || 'Search'}
         value={state.localSearchValue}
         onChange={handleSearchChange}
         onKeyDown={handleSearchKeyDown}
         InputProps={{
            endAdornment: (
               <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginRight: '0px'
               }}>
                  {isSearchLoading && <CircularProgress size={16} color="inherit"/>}
                  {!isSearchLoading &&
                     <div style={{
                        backgroundColor: '#e0e0e0',
                        marginRight: '-12px',
                        padding: '12px'
                     }}>
                        {count !== undefined && <>{count}</>}
                     </div>}
               </div>
            )
         }}
         onFocus={event => {
            event.target.select();
         }}
         onSubmit={e => {
            e.preventDefault();
            return false;
         }}
         defaultValue={state.localSearchValue}
      />
   );

   const updateHeaders = useCallback(() => {
      const sortId = getValues('sort');

      const filterAnd = getValues('filterAnd');
      const filterOr = getValues('filterOr');

      onHeaderChanged({
         ...headerData,
         sort: sortId,
         filterAnd,
         filterOr
      });
   }, [getValues, headerData, onHeaderChanged]);

   // Keep a ref to the latest updateHeaders to avoid stale closures in debounce
   const updateHeadersRef = useRef(updateHeaders);
   updateHeadersRef.current = updateHeaders;

   // Debounced version of updateHeaders for filter selections
   // Uses ref to always call latest version without recreating the callback
   const updateHeadersDebounced = useCallback(() => {
      if (filterDebounceTimerRef.current) {
         clearTimeout(filterDebounceTimerRef.current);
      }
      filterDebounceTimerRef.current = setTimeout(() => {
         updateHeadersRef.current();
      }, FILTER_DEBOUNCE_MS);
   }, []);

   let maxFiltersOnLine = isDesktopLG ? 2 : isDesktopMD ? 1 : 0;

   // Fixed proportions to prevent layout flickering
   const searchWidth = isDesktopLG ? 40 : isDesktopMD ? 50 : 100;
   const filtersWidth = 100 - searchWidth;

   // Shared filter renderer to ensure DRY principle
   const renderFilter = (filter: RangeFilter<any>) => (
      <FormInputSelectMultiple
         icon={<FilterAlt/>}
         name={'filterOr.' + filter.id}
         label={filter.label}
         onChange={updateHeadersDebounced}
         allowSearch
         options={
            filter?.options?.map(option => ({
               id: option.id || option.label,
               label: option.label,
               subtext: option.subtext
            })) || []
         }
      />
   );

   return <div {...props} className={classes.container}>
      <FormProvider {...methods}>
         <Drawer variant={"persistent"} open={state.filterPanel} anchor={"right"} onClose={() => dispatch({ type: 'TOGGLE_FILTER_PANEL', payload: false })}
                 BackdropProps={{invisible: true}}>
            <Stack direction={"column"} spacing={1} className={classes.filterDrawer}>
               <TitleText type={"h2"}>More filters</TitleText>
               {filters?.map(filter => (
                  <Grid item xs key={'range_filter_drawer_' + filter.id}>
                     {renderFilter(filter)}
                  </Grid>
               ))}
               <Button variant={"contained"} color={"primary"} onClick={() => dispatch({ type: 'TOGGLE_FILTER_PANEL', payload: false })}>
                  Done
               </Button>
            </Stack>
         </Drawer>
         <Stack direction={"row"} spacing={1}>
            <div style={{width: searchWidth + '%', flexShrink: 0}}>
               {renderSearchInput()}
            </div>
            {filtersWidth > 0 && <div style={{width: filtersWidth + '%', flexShrink: 0}}>
               <Stack direction={"row"} spacing={1}>
                  {filters?.slice(0, maxFiltersOnLine).map(filter => (
                     <Grid item xs key={'range_filter_inline_' + filter.id}>
                        {renderFilter(filter)}
                     </Grid>
                  ))}
                  {(filters || []).length > maxFiltersOnLine && <div className={classes.button}>
                     <Button
                        color={"secondary"}
                        size={"small"}
                        variant={"outlined"}
                        style={{borderColor: '#d0d0d0'}}
                        onClick={() => dispatch({ type: 'TOGGLE_FILTER_PANEL', payload: true })}>
                        Filters
                     </Button>
                  </div>}

                  {(sorts || []).length > 0 && <Grid item xs>
                     <FormInputSelect
                        icon={<Sort/>}
                        name={"sort"}
                        label={"Sort"}
                        onSelectChange={() => updateHeaders()}
                        options={sorts!.map(sort => ({
                           id: sort.id,
                           name: sort.label
                        }))}/>
                  </Grid>}
                  <RangeCustomizer globalId={globalId}/>
               </Stack>
            </div>}
         </Stack>
      </FormProvider>
   </div>
}

function useDebounce<TValue>(value: TValue, delay: number = 300) {
   const [debouncedValue, setDebouncedValue] = useState(value)

   useEffect(() => {
      const handler = setTimeout(() => {
         setDebouncedValue(value)
      }, delay)

      return () => {
         clearTimeout(handler)
      }
   }, [value, delay])

   return debouncedValue
}

const useStyles = makeStyles({
   container: {
      minHeight: '56px',
      boxSizing: 'border-box',
   },
   filterDrawer: {
      minWidth: '500px',
      padding: '20px'
   },
   button: {
      marginBottom: '8px !important',
      "& button": {
         height: '100%'
      }
   }
});
